import mockData from './mockParticipants.json';
import { TEAM_ROSTER_DEFAULT } from '../participantConstants';

/**
 * The concrete "mock" implementation of this module's participants provider
 * — the only place in the whole tournament_formats module that knows real
 * participant/team data doesn't exist yet. Nothing in here is imported
 * directly by useScheduleBuilder.js or any wizard step: ScheduleBuilderTab.jsx
 * is the one file that wires `{ getParticipants, getMaxParticipants }` (see
 * exports below) into useScheduleBuilder's `participantsProvider` hook
 * option. Swapping either entry point (standalone Explore Formats, or a real
 * tournament's Schedule tab) to a real enrolled-roster provider later means
 * writing a new module with the same two-function shape and changing what
 * ScheduleBuilderTab.jsx passes in — nothing else in this module changes.
 */

const poolFor = (participantType) => {
  if (participantType === 'teams') return mockData.teams;
  if (participantType === 'doubles') return mockData.doubles;
  return mockData.singles;
};

/**
 * A team's stored roster (mockParticipants.json) only ever has 4 named,
 * gendered players — that's all MLP-style pairing (tieFormats.isMLPEligible,
 * which only ever checks for exactly 4) ever needs. Requesting more than
 * that for team sports with bigger rosters extends with filler bench
 * players borrowed from the singles pool, offset per team (by seed) so
 * different teams don't all show identical bench names. These filler
 * players have no gender set and never affect MLP eligibility either way —
 * they only exist to make roster sizes above 4 look populated.
 */
const buildRoster = (team, playersPerTeam) => {
  if (playersPerTeam <= team.roster.length) return team.roster.slice(0, playersPerTeam);
  const fillerPool = mockData.singles;
  const extra = [];
  for (let i = team.roster.length; i < playersPerTeam; i++) {
    const src = fillerPool[(team.seed * 7 + i) % fillerPool.length];
    extra.push({ id: `${team.id}-b${i + 1}`, name: src.name, gender: null });
  }
  return [...team.roster, ...extra];
};

/**
 * Returns `count` mock participants of the given type, shaped as
 * { id, name, seed } regardless of singles/doubles — doubles' two-player
 * roster still collapses to one display name ("Beaumont/Schmidt") since
 * every generator only ever schedules "slots", not individual players.
 *
 * 'teams' is the one exception: it keeps its `roster` (built to
 * `playersPerTeam` players — see buildRoster above) alongside { id, name,
 * seed }, because formats that need team rosters (cross_group's MLP-style
 * game slate) schedule specific players *within* a team, not just the team
 * as an opaque slot.
 */
export const getMockParticipants = (participantType, count, playersPerTeam = TEAM_ROSTER_DEFAULT) => {
  const pool = poolFor(participantType);
  const n = Math.max(0, Math.min(count, pool.length));
  return pool.slice(0, n).map((entry) => ({
    id: entry.id,
    name: entry.name,
    seed: entry.seed,
    ...(participantType === 'teams' ? { roster: buildRoster(entry, playersPerTeam) } : {}),
  }));
};

export const getMockPoolSize = (participantType) => poolFor(participantType).length;
